import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MultipleEmailValidator } from 'src/app/shared/validators/multiple-email.validator';
import { debounceTime, map, Observable, of, startWith, Subject, switchMap } from 'rxjs';
import { PrecastService } from 'src/app/shared/services/precast.service';
import { LookupService } from 'src/app/shared/services/lookup.service';
import { UserService } from '../../../../shared/services/user.service';
import { ICompanyData, ICompanyDetails } from '../../../../shared/models/i-company.modal';
import { IOfficeLocation } from '../../../../shared/models/i-office-location.modal';

@Component({
    selector: 'app-company-information',
    templateUrl: './company-information.component.html',
    styleUrls: ['./company-information.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CompanyInformationComponent implements OnInit, OnChanges, AfterViewInit {
  @Output() companyData: EventEmitter<ICompanyData> = new EventEmitter();
  @Output() companyDuplicate: EventEmitter<boolean> = new EventEmitter();
  @Input() submissionType: string;
  @Input() data: any;
  makeSampleEditable = true;
  @Input() set makeEditable(value: boolean) {
    this.makeSampleEditable = value;
  }

  initialLoad = true;
  companyFormGroup: FormGroup;
  rushToggleDisabled: boolean;
  companies: ICompanyDetails[];
  filteredCompanies: Observable<ICompanyDetails[]>;
  name = new FormControl();
  isUserLoggedin = false;
  isCompanyReadonly = false;
  public companyName: any = '';
  countries: any;
  bcountries: any;
  states: any;
  bstates:any;
  cities: any;
  bcities: any;
  officeLocations: IOfficeLocation[] = [];
  searchCompanyInput = '';
  copyControlField = [
    {
      control: 'contactName', cyControl: 'bContactName',
    },
    // {
    //   control: 'emails', cyControl: 'bContactEmail',
    // },
    {
      control: 'contactNumber', cyControl: 'bcontactNumber',
    },
    {
      control: 'streetAddress', cyControl: 'bstreetAddress',
    },
    {
      control: 'postalCode', cyControl: 'bpostalCode',
    },
    {
      control: 'countryId', cyControl: 'bcountryId',
    },
    {
      control: 'stateId', cyControl: 'bstateId',
    },
    {
      control: 'cityId', cyControl: 'bcityId',
    },
    {
      control: 'name', cyControl: 'invoice',
    },
  ];

  private fetchCompanySubject = new Subject<string>();
  companyDulplicateCheck = false;
  ocrCompanyDuplicate = false;
  loadForm = false;
  constructor(
    private fb: FormBuilder,
    private staticDataService: LookupService,
    private dashboardService: PrecastService,
    public dialog: MatDialog,
    public userService: UserService,
  ) {
    this.userService.userAccount.subscribe((account) => {
      this.isUserLoggedin = !!account;
      // Staff get their Entra-scoped offices, customers and guests get every active office
      this.fetchOfficeLocations();
    });
    this.companyFormGroup = this.fb.group({
      companyId: [''],
      projectId: [''],
      bId: [''],
      name: ['', Validators.required],
      contactNumber: ['', Validators.required],
      emails: ['', [Validators.required, Validators.email, this.noCommasValidator]],
      contactName: ['', Validators.required],
      streetAddress: [''],
      countryId: [''],
      stateId: [''],
      cityId: [''],
      postalCode: [''],
      reportSubmissionDate: [''],
      hasSameBilling: [false],
      invoice: [''],
      bcontactNumber: [''],
      bemails: ['', MultipleEmailValidator.comaSeperatedEmailValidator()],
      bstreetAddress: [''],
      bcountryId: [''],
      bstateId: [''],
      bcityId: [''],
      bpostalCode: [''],
      projectName: ['', Validators.required],
      projectLocation: [''],
      // Required for staff and customers alike
      officeLocationId: [null, Validators.required],
      jobNumber: [''],
      projectNumber: [''],
      geothermField: [false],
      instructionsConfirmed: [false],
      rushTesting: [false],
      preApproval: [
        {
          value: false,
          disabled: !this.makeSampleEditable,
        },
      ],
      projectStatusTypeId: [null],
      bContactName: ['', Validators.required],
      bContactEmail: ['', [Validators.required, MultipleEmailValidator.comaSeperatedEmailValidator()]],
      // dueDate: [''],
      inHouseIdPrefix: ['', [Validators.pattern('^[A-Z]{0,2}$')]],
    });
  }

  ngOnInit(): void {
    this.fetchCountries();
    if (this.data && this.data?.companyId) {
      this.isCompanyReadonly = true;
    }
    if (this.data) {
      this.getCompanyState(this.data?.countryId, false);
      this.getCompanyCities(this.data?.stateId, false);
      if (!this.data?.hasSameBilling) {
        this.getBillingStates(this.data?.bcountryId, false);
        this.getBillingCities(this.data?.bstateId, false);
      }
      this.rushToggleDisabled = this.data.isRushToggleEnabled;
      if (this.data.contactNumber) {
        this.data.contactNumber = this?.data?.contactNumber.slice(0, 10).replace(/^(\d{0,3})(\d{0,3})(\d{0,4})/, '($1) $2-$3');
      }

      if (this.data.bcontactNumber) {
        this.data.bcontactNumber = this?.data?.bcontactNumber
          .slice(0, 10)
          .replace(/^(\d{0,3})(\d{0,3})(\d{0,4})/, '($1) $2-$3');
      }
      this.companyFormGroup.patchValue(this.data);
      this.selectSingleOfficeLocation();
      this.checkHasBillingInfo();
      this.companyFormGroup.markAsDirty();
    }
    this.companyFormGroup?.valueChanges?.subscribe((frmGroup) => {
      let validForm = this.companyFormGroup?.valid;
      // checking null value
      const v = this.companyFormGroup?.value?.contactNumber
        ? this.companyFormGroup.value.contactNumber.replace(/[^0-9]/g, '')
        : '';

      if (v.length < 10) {
        this.companyFormGroup?.controls['contactNumber'].setErrors({
          validContactNumber: false,
        });
        validForm = false;
      }
      // checking null value
      const bv = this.companyFormGroup?.value?.bcontactNumber
        ? this.companyFormGroup.value.bcontactNumber.replace(/[^0-9]/g, '')
        : '';
      if (this.companyFormGroup.value.bcontactNumber) {
        if (bv.length > 0 && bv.length < 10) {
          this.companyFormGroup?.controls['bcontactNumber'].setErrors({
            validbContactNumber: false,
          });
          validForm = false;
        }
      }
      if (!frmGroup.preApproval) {
        frmGroup.preApproval = this.companyFormGroup.getRawValue().preApproval;
      }
      this.companyData?.emit({
        value: frmGroup,
        dirty: this.companyFormGroup?.dirty,
        valid: validForm,
      });
    });
    this.setFormControlState();
    if (this.data) {
      // Report the real validity of saved data (e.g. a customer project with no office) instead of
      // leaving the parent's assumed "valid"; setFormControlState doesn't always change a value to trigger it
      this.companyFormGroup.updateValueAndValidity();
    }
    this.searchCompanyInput = this.companyFormGroup?.controls['name'].value;

    this.fetchCompanySubject
      .pipe(
        debounceTime(300), // Add a 300ms delay
        switchMap((searchCompanyName: string) => this.dashboardService?.fetchCompanyDetail(searchCompanyName)),
      )
      .subscribe({
        next: (response: ICompanyDetails) => {
          this.companies = Object.assign([], response);
          this.filteredCompanies = this.companyFormGroup.controls['name'].valueChanges.pipe(
            startWith(''),
            map((value) => this.filterStates(value || '')),
          );
          this.checkForDuplicateCompany();
        },
      });

    this.initialLoad = false;
  }
  ngAfterViewInit(): void {
    if(this.data && this.data?.companyId === undefined || this.data?.companyId === null ){
      const cName  = this.data?.name;
      if(cName.trim() !== '') {
        this.dashboardService?.fetchCompanyDetail(cName).subscribe({
          next: (response: ICompanyDetails) => {
            this.companies = Object.assign([], response);
            if(this.companies && this.companies.length > 0 ) {
              const exists = this.companies.findIndex((x) => x.name.toLowerCase() === cName.toLowerCase());
              if(exists > -1) {
                this.ocrCompanyDuplicate = true;
                const control =  this.getControl('name');
                control.setErrors({
                  duplicateCompany: true,
                });
                this.companyDuplicate.emit(true);
                this.companyFormGroup.markAllAsTouched();
                this.filteredCompanies = of(this.companies);

              } else {
                this.companyDuplicate.emit(false);
              }
            }
          },
        });
      }
    }
  }

  fetchCountries(): void {
    this.staticDataService?.fetchCountries()?.subscribe({
      next: (response) => {
        this.countries = response;
        this.bcountries = response;
      },
    });
  }

  fetchOfficeLocations(): void {
    const isStaffRequest = this.isUserLoggedin;
    const officeLocations$ = isStaffRequest
      ? this.staticDataService?.fetchOfficeLocations()
      : this.staticDataService?.fetchPublicOfficeLocations();
    officeLocations$?.subscribe({
      next: (response) => {
        // The account can arrive while the guest list is loading; never let a stale list replace the current one
        if (isStaffRequest !== this.isUserLoggedin) {
          return;
        }
        this.officeLocations = Array.isArray(response) ? response : [];
        this.selectSingleOfficeLocation();
      },
    });
  }

  // A user with a single office location gets it selected automatically
  selectSingleOfficeLocation(): void {
    const officeLocationControl = this.companyFormGroup?.get('officeLocationId');
    if (this.officeLocations.length === 1 && officeLocationControl && !officeLocationControl.value) {
      officeLocationControl.setValue(this.officeLocations[0].id);
    }
  }

  getCompanyState(countryId: string, template: boolean) {
    if (Number(countryId) > 0) {
      this.staticDataService?.fetchState(countryId)?.subscribe({
        next: (response) => {
          this.states = response;
          const hasSameBilling = this.data?.hasSameBilling || this.getControl('hasSameBilling')?.value;
          if (!template) {
            const patchValue = this.getControl('stateId').value;
            this.getControl('stateId').patchValue(null);
            this.getControl('stateId').patchValue(patchValue);
          } else {
            this.getControl('stateId').patchValue(null);
            this.getControl('cityId').patchValue(null);
          }

          if (hasSameBilling) {
            this.bstates = response;
            this.checkCopyEnabled('countryId', 'bcountryId');
            this.checkCopyEnabled('stateId', 'bstateId');
            this.checkCityCopy();
          }
        },
      });
      this.cities = [];
    } else {
      this.states = [];
      this.cities = [];
      this.getControl('stateId').patchValue(null);
      this.getControl('cityId').patchValue(null);
      const hasSameBilling = this.data?.hasSameBilling || this.getControl('hasSameBilling')?.value;
      if (hasSameBilling) {
        this.bstates = [];
        this.bcities = [];
        this.getControl('bcountryId').patchValue(null);
        this.getControl('bstateId').patchValue(null);
        this.getControl('bcityId').patchValue(null);
      }
    }
  }

  getCompanyCities(stateId : string, template: boolean) {
    if (Number(stateId) > 0) {
      this.staticDataService?.fetchCities(stateId)?.subscribe({
        next: (response) => {
          this.cities = response;
          if (!template) {
            const patchValue = this.getControl('cityId').value;
            this.getControl('cityId').patchValue(null);
            this.getControl('cityId').patchValue(patchValue);
          } else {
            this.getControl('cityId').patchValue(null);
          }
          const hasSameBilling = this.data?.hasSameBilling || this.getControl('hasSameBilling')?.value;
          if (hasSameBilling) {
            this.bcities = response;
            this.checkCopyEnabled('stateId', 'bstateId');
            this.checkCityCopy();
          }
        },
      });
    } else {
      this.getControl('cityId').patchValue(null);
      this.cities = [];
      const hasSameBilling = this.data?.hasSameBilling || this.getControl('hasSameBilling')?.value;
      if (hasSameBilling) {
        this.getControl('bcityId').patchValue(null);
        this.getControl('bstateId').patchValue(null);
      }
    }
  }

  getBillingStates(countryId: string, template: boolean) {
    if (Number(countryId) > 0) {
      this.staticDataService?.fetchState(countryId)?.subscribe({
        next: (response) => {
          this.bstates = response;
          if (!template) {
            const patchValue = this.getControl('bstateId').value;
            this.getControl('bstateId').patchValue(null);
            this.getControl('bstateId').patchValue(patchValue);
          } else {
            this.getControl('bstateId').patchValue(null);
          }
        },
      });
      this.bcities = [];
    } else {
      this.bstates = [];
      this.getControl('bstateId').patchValue(null);
      this.getControl('bcityId').patchValue(null);
    }
  }

  getBillingCities(stateId: string, template: boolean) {
    if (Number(stateId) > 0) {
      this.staticDataService?.fetchCities(stateId)?.subscribe({
        next: (response) => {
          this.bcities = response;
          if (!template) {
            const patchValue = this.getControl('bcityId').value;
            this.getControl('bcityId').patchValue(null);
            this.getControl('bcityId').patchValue(patchValue);
          } else {
            this.getControl('bcityId').patchValue(null);
          }
        },
      });
    } else {
      this.bcities = [];
      this.getControl('bcityId').patchValue(null);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['makeEditable'] || changes['submissionType']) {
      this.setFormControlState();
    }
  }

  setFormControlState(): void {
    const geothermFieldControl = this.companyFormGroup.get('geothermField') as FormControl;
    const preApprovalControl = this.companyFormGroup.get('preApproval') as FormControl;
    // const rushTestingControl = this.companyFormGroup.get('rushTesting') as FormControl;

    // For non-native-soil submissions always set the geotherm field to false and disable it.
    if (this.submissionType === 'native-soil') {
      // For native-soil ensure default is false (No) when not explicitly provided
      if (
        geothermFieldControl.value === null ||
        geothermFieldControl.value === undefined ||
        geothermFieldControl.value === ''
      ) {
        geothermFieldControl.setValue(false);
      }
    } else {
      // When not native-soil, always set to false but keep control enabled (hidden in template)
      geothermFieldControl.setValue(false);
    }

    if (this.makeSampleEditable) {
      preApprovalControl.enable();
    } else {
      preApprovalControl.disable();
    }

    geothermFieldControl.updateValueAndValidity();
    preApprovalControl.updateValueAndValidity();
  }

  get duplicateCompanyErrors(): any {
    return this.companyFormGroup.get('name')?.errors;
  }
  get emailsError(): any {
    return this.companyFormGroup?.controls['emails']?.errors
      ? this.companyFormGroup?.controls['emails']?.errors
      : false;
  }

  get bemailsError(): any {
    return this.companyFormGroup?.controls['bemails']?.errors
      ? this.companyFormGroup?.controls['bemails']?.errors
      : false;
  }

  get contactEmailsError(): any {
    return this.companyFormGroup?.controls['bContactEmail']?.errors
      ? this.companyFormGroup?.controls['bContactEmail']?.errors
      : false;
  }

  get contactNumberErrors(): any {
    return this.companyFormGroup?.controls['contactNumber']?.errors
      ? this.companyFormGroup?.controls['contactNumber']?.errors
      : false;
  }

  get bcontactNumberErrors(): any {
    return this.companyFormGroup?.controls['bcontactNumber']?.errors
      ? this.companyFormGroup?.controls['bcontactNumber']?.errors
      : false;
  }

  setCopy(): void {
    const copyControl = [...this.copyControlField];
    copyControl.forEach((control: any) => {
      this.copyControl(control.control, control.cyControl);
    });
  }

  setControlDisableOnSameASAbove(): void {
    const copyControl = [...this.copyControlField];
    copyControl.forEach((control: any) => {
      if (this.getControl(control.cyControl)) {
        this.getControl(control.cyControl).disable();
        this.getControl(control.cyControl).updateValueAndValidity();
      }
    });
  }

  setControlEnableOnSameASAbove(): void {
    const copyControl = [...this.copyControlField];
    copyControl.forEach((control: any) => {
      if (this.getControl(control.cyControl)) {
        this.getControl(control.cyControl).enable();
        this.getControl(control.cyControl).updateValueAndValidity();
      }
    });
  }

  checkCopyEnabled(controlName:string, copyControlName:string): void {
    if (this.getControl('hasSameBilling').value) {
      this.copyControl(controlName, copyControlName);
    }
  }

  getControl(controlName: string): FormControl {
    return this.companyFormGroup?.get(controlName) as FormControl;
  }

  copyControl(controlName: string, copyControlName: string): void {
    if (this.getControl(controlName) && this.getControl(copyControlName)) {
      this.getControl(copyControlName).patchValue(this.getControl(controlName).value);
      this.getControl(copyControlName).updateValueAndValidity();
    }
  }

  checkCityCopy(): void {
    this.checkCopyEnabled('cityId', 'bcityId');
  }

  checkHasBillingInfo(): void {
    const check = this.companyFormGroup?.controls['hasSameBilling']?.value;
    if (check) {
      this.bcountries = this.countries;
      this.bstates = this.states;
      this.bcities = this.cities;
      this.setCopy();
      this.setControlDisableOnSameASAbove();
    } else {
      this.setControlEnableOnSameASAbove();
    }
  }

  clearBillingInfo(): void {
    this.companyFormGroup?.controls['invoice']?.setValue('');
    this.companyFormGroup?.controls['bemails']?.setValue('');
    this.companyFormGroup?.controls['bcontactNumber']?.setValue('');
    this.companyFormGroup?.controls['bContactName']?.setValue('');
    this.companyFormGroup?.controls['bContactEmail']?.setValue('');
    this.companyFormGroup?.controls['bstreetAddress']?.setValue('');
    this.companyFormGroup?.controls['bcountryId']?.setValue('');
    this.companyFormGroup?.controls['bcityId']?.setValue('');
    this.companyFormGroup?.controls['bstateId']?.setValue('');
    this.companyFormGroup?.controls['bpostalCode']?.setValue('');
  }

  fetchCompanyDetails(searchCompanyName: string) {
    this.fetchCompanySubject.next(searchCompanyName);
  }

  getCompanyDetail() {
    const selected = this.getControl('name').value;
    if (selected && !this.isCompanyReadonly) {
      this.fetchCompanyDetails(selected);
    }
  }

  private filterStates(value: string): ICompanyDetails[] {
    const filterValue = value.toLowerCase();
    // Filter companies based on the input value
    return this.companies.filter((company) => company.name.toLowerCase().includes(filterValue));
  }

  onOptionSelected(event: any): void {
    this.companyFormGroup.controls['companyId'].patchValue(event.option.id);
    this.companyFormGroup.controls['name'].patchValue(event.option.value);
    this.companyFormGroup.controls['companyId'].updateValueAndValidity();
    this.checkCopyEnabled('name', 'invoice');
    this.companyFormGroup.controls['name'].updateValueAndValidity();
    this.companyDuplicate.emit(false);
    this.isCompanyReadonly = true;
  }

  removeExitsingCompany(): boolean {
    const companyControl =  this.getControl('name');
    const companyIdControl =  this.getControl('companyId');
    companyIdControl.patchValue(null);
    companyControl.patchValue('');
    companyControl.setErrors({
      required: true,
    });
    companyIdControl.updateValueAndValidity();
    companyControl.updateValueAndValidity();
    this.companyDulplicateCheck = false;
    this.searchCompanyInput = '';
    this.isCompanyReadonly = false;
    this.filteredCompanies = of([]);
    this.checkCopyEnabled('name', 'invoice');
    return false;
  }

  companyFocusOut(): void {
    this.getControl('name').patchValue(this.searchCompanyInput);
  }
  checkForDuplicateCompany(): void{
    const companyControl = this.getControl('name');
    const typedCompany = companyControl.value;
    if(!this.isCompanyReadonly) {
        if(this.companies && this.companies.length > 0 ) {
              const exists = this.companies.findIndex((x) => x.name.toLowerCase() === typedCompany.toLowerCase());
              if(exists > -1) {
                this.companyDulplicateCheck =true;
                this.companyDuplicate.emit(true);
                const control =  this.getControl('name');
                control.setErrors({
                  duplicateCompany: true,
                });
              } else {
                this.companyDuplicate.emit(false);
              }
        } else {
          this.companyDuplicate.emit(false);
        }
    } else {
      this.companyDuplicate.emit(false);
    }

  }

  onEmailInput(event: any): void {
    const initialValue = this.companyFormGroup.controls['emails'].value;
    this.companyFormGroup.controls['emails'].setValue(initialValue.replace(/[^a-zA-Z0-9@._-]/g, ''));
    event.target.value = event.target.value.replace(/[^a-zA-Z0-9@._-]/g, '');
  }

  onEmailPaste(event: any): void {
    event.preventDefault();
    const pastedText = event.clipboardData.getData('text');
    const cleanedText = pastedText.replace(/,/g, '').trim();

    if (this.isValidEmail(cleanedText)) {
      this.companyFormGroup.controls['emails'].setValue(cleanedText);
    } else {
      this.companyFormGroup.controls['emails'].setValue('');
    }
  }

  noCommasValidator(control: FormControl) {
    if (control.value && control.value.includes(',')) {
      return {
        noCommas: true
      };
    }
    return null;
  }

  isValidEmail(email: string): boolean {
      const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return emailRegex.test(email);
  }
}
